import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';

import BlockIcon from '../../../../../../assets/icons/BlockIcon';
import NoStrikeIcon from '../../../../../../assets/icons/NoStrikeIcon';
import OneStrikeIcon from '../../../../../../assets/icons/OneStrikeIcon';
import ThreeStrikesIcon from '../../../../../../assets/icons/ThreeStrikesIcon';
import TwoStrikesIcon from '../../../../../../assets/icons/TwoStrikesIcon';
import CheckBox from '../../../../../../components/CheckBox/CheckBox';
import LabeledField from '../../../../../../components/LabeledField';
import { SelectDropdownFilter } from '../../../../../../components/SelectDropdownFilter/SelectDropdownFilter';
import { selectUserListData } from '../../../../../../store/selectors/userListSelector';
import theme from '../../../../../../theme';
import { PRIMARY_COLOR } from '../../../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../../../theme/fontConstants';
import { epochToDateInReadableFormat, epochToTimeInReadableFormat } from '../../../../../../utils';
import { MAX_STRIKES } from '../../../../UsersConstants';
import { getCandidateAttendance, updateInfraction } from '../../../../usersListReducer';

import InfractionCard from './InfractionCard';
import SuspensionCard from './SuspensionCard';

const styles = theme?.components?.userDetail;
const limit = 5;

const isSuspensionActive = (date) => date > Date.now();

const getStrikeInfo = (strikeNumber, suspensionsLength) => {
  switch (strikeNumber) {
    case MAX_STRIKES:
      return {
        icon: <ThreeStrikesIcon sx={styles.iconStyle} />,
        subText: LanguageConverterV2('userDetail.attendance.threeStrikes'),
      };
    case 2:
      return {
        icon: <TwoStrikesIcon sx={styles.iconStyle} />,
        subText: LanguageConverterV2('userDetail.attendance.twoStrikes'),
      };
    case 1:
      return {
        icon: <OneStrikeIcon sx={styles.iconStyle} />,
        subText: LanguageConverterV2('userDetail.attendance.oneStrike'),
      };
    default:
      return {
        icon: <NoStrikeIcon sx={styles.iconStyle} />,
        subText:
          suspensionsLength > 0
            ? LanguageConverterV2('userDetail.attendance.noStrike')
            : LanguageConverterV2('userDetail.attendance.noStrikeNoSuspensions'),
      };
  }
};

const AttendanceStrikeBox = ({ selectedUser }) => {
  const usersListData = useSelector(selectUserListData);

  const {
    candidateAttendance,
    isLoadingCandidateAttendance,
    isUpdatingInfraction,
    shouldReloadCandidateAttendance,
  } = usersListData;

  const [filters, setFilters] = useState({
    showExcused: false,
    suspensionFilter: [],
    filterToPlacementIds: [],
    page: 0,
  });

  const dispatch = useDispatch();
  const infractions = candidateAttendance?.placements || [];

  const getActiveSuspension = (items) => {
    if (!items || items.length === 0) return null;

    const itemWithLatestEnd = items.reduce((maxItem, currentItem) =>
      currentItem.end > maxItem.end ? currentItem : maxItem
    );
    return isSuspensionActive(itemWithLatestEnd?.end) ? itemWithLatestEnd : null;
  };
  const suspensions = candidateAttendance?.suspensions;
  const activeSuspension = getActiveSuspension(suspensions);

  const strikeCount = activeSuspension
    ? MAX_STRIKES
    : candidateAttendance?.currentInfractions?.length || 0;
  const suspensionsLength = suspensions?.length || 0;

  const strikeInfo = getStrikeInfo(strikeCount, suspensionsLength);

  useEffect(() => {
    if (selectedUser?.id) {
      const { showExcused, page, filterToPlacementIds } = filters;

      const payload = {
        candidateId: selectedUser?.id,
        showExcused,
        ...(filterToPlacementIds?.length > 0 && { filterToPlacementIds }),
        limit,
        page,
      };
      dispatch(getCandidateAttendance(payload));
    }
  }, [dispatch, filters, selectedUser?.id]);

  useEffect(() => {
    if (shouldReloadCandidateAttendance) {
      const { showExcused, page, filterToPlacementIds } = filters;

      const payload = {
        candidateId: selectedUser?.id,
        showExcused,
        ...(filterToPlacementIds?.length > 0 && { filterToPlacementIds }),
        limit,
        page,
      };
      dispatch(getCandidateAttendance(payload));
    }
  }, [dispatch, filters, selectedUser?.id, shouldReloadCandidateAttendance]);

  const handleOnViewExcusedChange = (e) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: 0,
      showExcused: e.target.checked,
    }));
  };

  const filterOptions =
    suspensions?.map((suspension) => ({
      name: suspension.name,
      value: suspension.name,
    })) || [];

  const handleSuspensionFilterChange = useCallback(
    (event) => {
      const placementIds = [];

      suspensions?.forEach((suspension) => {
        if (event.includes(suspension.name)) {
          placementIds.push(...suspension.placementIds);
        }
      });
      setFilters((prevFilters) => ({
        ...prevFilters,
        page: 0,
        filterToPlacementIds: placementIds,
        suspensionFilter: event,
      }));
    },
    [suspensions]
  );

  const handleOnPageChange = (newPage) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: newPage,
    }));
  };

  const handleOnExcuseOrReinstate = useCallback(
    (placement) => {
      dispatch(
        updateInfraction({
          id: placement?.placementId,
          fields: { calcAttendance: { [placement?.infractionType]: !placement?.excused } },
        })
      );
    },
    [dispatch]
  );
  const totalListCount = candidateAttendance?.placementsCount || 0;
  const currentPagesStart = totalListCount ? limit * filters.page + 1 : 0;
  const currentPagesEnd =
    currentPagesStart + limit - 1 < totalListCount ? currentPagesStart + limit - 1 : totalListCount;

  return (
    <Box>
      <Box>
        <Typography sx={{ ...styles.headerStyle }} data-testid="attendance-header">
          {LanguageConverterV2('userDetail.attendance.header')}
        </Typography>
        <Card variant="outlined" sx={styles.attendanceContainer}>
          <CardContent sx={{ height: '100%', padding: 2 }}>
            <Box justifyContent="space-between" display="flex">
              <Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography sx={styles.headerStyle} data-testid="attendance-strike-box-header">
                    {LanguageConverterV2('userDetail.attendance.strike')} ({strikeCount}/
                    {MAX_STRIKES})
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {strikeInfo?.icon}
                  </Box>
                </Box>
                <Typography sx={styles.subText}>{strikeInfo?.subText}</Typography>
                <Typography />
                {activeSuspension && (
                  <Typography sx={styles.subText}>
                    {LanguageConverterV2('userDetail.attendance.suspensionLift', {
                      time: epochToTimeInReadableFormat(activeSuspension?.end),
                      date: epochToDateInReadableFormat(activeSuspension?.end),
                    })}
                  </Typography>
                )}
              </Box>
              <Box>
                <CheckBox
                  onChange={handleOnViewExcusedChange}
                  disabled={isLoadingCandidateAttendance || isUpdatingInfraction}
                  checked={filters.showExcused}
                  label={LanguageConverterV2('userDetail.attendance.showExcused')}
                  name="viewExcused"
                  variant="standard"
                  fullWidth
                  sx={{
                    '& .MuiFormControlLabel-label': styles.checkBoxLabel,
                  }}
                />
                {suspensions?.length > 0 && (
                  <LabeledField
                    title={LanguageConverterV2('userDetail.attendance.filterLabelSuspensions')}
                    field={
                      <SelectDropdownFilter
                        disabled={isLoadingCandidateAttendance || isUpdatingInfraction}
                        placeholder={LanguageConverterV2(
                          'userDetail.attendance.filterLabelSuspensions'
                        )}
                        isMultiSelectable
                        onValueChange={handleSuspensionFilterChange}
                        options={filterOptions}
                        getOptionLabel={(option) => option.name}
                        wrapperSx={{ width: '250px' }}
                        startAdornmentIcon={
                          <BlockIcon
                            sx={{
                              height: 16,
                              width: 16,
                              color: PRIMARY_COLOR[50],
                              marginTop: '3px',
                              marginLeft: '4px',
                            }}
                          />
                        }
                        initialValue={
                          (filters.suspensionFilter.length > 0 &&
                            filters.suspensionFilter.map((val) => ({
                              name: val,
                              value: val,
                            }))) ||
                          []
                        }
                      />
                    }
                  />
                )}
              </Box>
            </Box>
            {isLoadingCandidateAttendance || isUpdatingInfraction ? (
              <CircularProgress color="inherit" size={20} />
            ) : (
              <>
                {suspensions?.length > 0 &&
                  suspensions?.map((suspension) => (
                    <SuspensionCard key={suspension?.name} suspension={suspension} />
                  ))}
                {infractions?.length > 0 &&
                  infractions?.map((infraction) => (
                    <InfractionCard
                      key={infraction.placementId}
                      infraction={infraction}
                      suspensionName={
                        suspensions?.find((s) => s.placementIds?.includes(infraction.placementId))
                          ?.name
                      }
                      handleOnExcuseOrReinstate={handleOnExcuseOrReinstate}
                    />
                  ))}
              </>
            )}
            <Grid container justifyContent="flex-end" alignItems="center" width="100%" gap={1}>
              <Typography sx={{ fontSize: '16px', fontFamily: PRIMARY_FONT[300] }}>
                {`${currentPagesStart} - ${currentPagesEnd} of ${totalListCount}`}
              </Typography>
              <IconButton
                data-testid="strike-box-previous-page-button"
                disabled={filters.page === 0 || totalListCount === 0}
                onClick={() => handleOnPageChange(filters.page - 1)}
              >
                <KeyboardArrowLeftIcon />
              </IconButton>
              <IconButton
                data-testid="strike-box-next-page-button"
                disabled={
                  !totalListCount || Math.ceil((totalListCount || 0) / limit) === filters.page + 1
                }
                onClick={() => handleOnPageChange(filters.page + 1)}
              >
                <KeyboardArrowRightIcon />
              </IconButton>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

AttendanceStrikeBox.propTypes = {
  selectedUser: PropTypes.shape({
    id: PropTypes.string,
  }),
};

export default AttendanceStrikeBox;
